import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/AggiornaAccountGenitore.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function AggiornaAccountGenitore() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [utente, setUtente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfilo, setSavingProfilo] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [formEdit, setFormEdit] = useState({
    username: "",
    attivo: true,
    ruoli: [],
  });

  const [newPassword, setNewPassword] = useState("");

  const generatePassword = (length = 10) => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "!@#$%&*?";
    const all = upper + lower + numbers + symbols;

    const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];

    let password = [
      pick(upper),
      pick(lower),
      pick(numbers),
      pick(symbols),
    ];

    for (let i = password.length; i < length; i++) {
      password.push(pick(all));
    }

    for (let i = password.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join("");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("it-IT");
  };

  const getRuoloDescrizione = (ruolo) => {
    if (!ruolo) return "";
    if (typeof ruolo === "string") return ruolo.replace("ROLE_", "");
    return ruolo.descrizione || ruolo.codice?.replace("ROLE_", "") || "";
  };

  const ruoloBadgeClass = (ruolo) => {
    switch (ruolo) {
      case "ROLE_ADMIN":
        return "badge-admin";
      case "ROLE_STAFF":
        return "badge-staff";
      case "ROLE_GENITORE":
        return "badge-genitore";
      default:
        return "";
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadUtente = async () => {
      setLoading(true);

      try {
        const res = await authFetch(`${API_URL}/api/genitore/utente/me`);

        if (!res.ok) {
          throw new Error("Errore nel recupero account");
        }

        const data = await res.json().catch(() => null);

        if (!data) {
          throw new Error("Dati account non disponibili");
        }

        if (mounted) {
          setUtente(data);
          setFormEdit({
            username: data.username || "",
            attivo: data.attivo ?? true,
            ruoli: Array.isArray(data.ruoli) ? data.ruoli : [],
          });
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          toast.error(err.message || "Errore nel caricamento account");
          setUtente(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadUtente();

    return () => {
      mounted = false;
    };
  }, [authFetch]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setFormEdit((prev) => ({
      ...prev,
      [name]: name === "username" ? value.toUpperCase() : value,
    }));
  };

  const salvaModifica = async () => {
  if (!formEdit.username.trim()) {
    toast.warning("Inserisci lo username");
    return;
  }

  setSavingProfilo(true);

  try {
    const usernamePrecedente = utente?.username || "";

    const payload = {
      username: formEdit.username.trim().toUpperCase(),
    };

    const res = await authFetch(`${API_URL}/api/genitore/utente/me`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error("Errore durante l'aggiornamento account");
    }

    const aggiornato = await res.json().catch(() => null);

    if (!aggiornato) {
      throw new Error("Risposta non valida dal server");
    }

    setUtente(aggiornato);
    setFormEdit({
      username: aggiornato.username || "",
      attivo: aggiornato.attivo ?? true,
      ruoli: Array.isArray(aggiornato.ruoli) ? aggiornato.ruoli : [],
    });

    const nuovoUsername = aggiornato.username || "";

    if (usernamePrecedente !== nuovoUsername) {
      toast.success(
        "Username aggiornato correttamente. Effettua il logout e accedi di nuovo prima di cambiare la password."
      );
    } else {
      toast.success("Account aggiornato correttamente");
    }
  } catch (err) {
    console.error(err);
    toast.error(err.message || "Errore durante l'aggiornamento account");
  } finally {
    setSavingProfilo(false);
  }
};

  const handleGenerateNewPassword = async () => {
    const generated = generatePassword(10);
    setNewPassword(generated);

    try {
      await navigator.clipboard.writeText(generated);
      toast.success("Nuova password generata e copiata");
    } catch {
      toast.success("Nuova password generata");
    }
  };

  const cambiaPassword = async () => {
    if (!newPassword.trim()) {
      toast.warning("Inserisci una nuova password");
      return;
    }

    setSavingPassword(true);

    try {
      const res = await authFetch(`${API_URL}/api/genitore/utente/me/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          newPassword: newPassword,
        }),
      });

      if (!res.ok) {
        throw new Error("Errore durante il cambio password");
      }

      setNewPassword("");
      toast.success("Password aggiornata correttamente");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Errore durante il cambio password");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <p className="utente-genitore-loading">Caricamento...</p>;
  }

  if (!utente) {
    return <p className="utente-genitore-loading">Impossibile caricare l'account.</p>;
  }

  return (
    <div className="utente-genitore-container" translate="no">
      <h2 className="utente-genitore-title">Modifica Account</h2>

      <div className="utente-genitore-card">
        <div className="utente-genitore-card-header">
          <span className="utente-genitore-card-label">Account</span>
        </div>

        <div className="utente-genitore-grid">
  <div className="utente-genitore-field utente-genitore-field-username">
    <label>Username</label>
    <input
      type="text"
      name="username"
      value={formEdit.username || ""}
      onChange={handleEditChange}
      disabled={savingProfilo}
    />
  </div>

  <div className="utente-genitore-field">
    <label>Stato</label>
    <div className="utente-genitore-value">
      <span className={formEdit.attivo ? "stato-attivo" : "stato-off"}>
        {formEdit.attivo ? "ATTIVO" : "DISATTIVO"}
      </span>
    </div>
  </div>

  <div className="utente-genitore-field">
    <label>Ruolo</label>
    <div className="utente-badge-wrapper">
      {(formEdit.ruoli || []).map((r, index) => {
        const codice = typeof r === "string" ? r : r.codice;
        const descrizione = getRuoloDescrizione(r);

        return (
          <span
            key={`${codice}-${index}`}
            className={`utente-badge ${ruoloBadgeClass(codice)}`}
          >
            {descrizione}
          </span>
        );
      })}
    </div>
  </div>

  <div className="utente-genitore-field">
    <label>Creato il</label>
    <div className="utente-genitore-value">
      {formatDateTime(utente.createdAt)}
    </div>
  </div>

  <div className="utente-genitore-field">
    <label>Aggiornato il</label>
    <div className="utente-genitore-value">
      {formatDateTime(utente.updatedAt)}
    </div>
  </div>
</div>

        <div className="utente-genitore-actions">
          <button
            type="button"
            onClick={salvaModifica}
            disabled={savingProfilo}
          >
            {savingProfilo ? "Salvataggio..." : "Salva Account"}
          </button>
        </div>
      </div>

      <div className="utente-genitore-card">
        <div className="utente-genitore-card-header">
          <span className="utente-genitore-card-label">Sicurezza</span>
        </div>

        <div className="utente-genitore-grid">
          <div className="utente-genitore-field utente-genitore-field-full">
            <label>Nuova Password</label>
            <input
              type="password"
              placeholder="Inserisci nuova password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={savingPassword}
            />
          </div>
        </div>

        <div className="utente-genitore-actions">
          <button
            type="button"
            className="utente-secondary-button"
            onClick={handleGenerateNewPassword}
            disabled={savingPassword}
          >
            Genera Password
          </button>

          <button
            type="button"
            onClick={cambiaPassword}
            disabled={savingPassword}
          >
            {savingPassword ? "Aggiornamento..." : "Cambia Password"}
          </button>
        </div>
      </div>

      <div className="utente-genitore-button-row">
        <button
          className="utente-genitore-custom-button"
          type="button"
          onClick={() => navigate("/gestionale2")}
          disabled={savingProfilo || savingPassword}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}